'use client'

import { useCallback, useEffect, useRef } from 'react'
import { FishingNaturePeriod } from '@/lib/fishing-day-phase'

interface NatureAudioGraph {
  context: AudioContext
  ambience: GainNode
  filter: BiquadFilterNode
  cricketTimer: number | null
}

function makeNoiseBuffer(context: AudioContext) {
  const buffer = context.createBuffer(1, context.sampleRate * 3, context.sampleRate)
  const data = buffer.getChannelData(0)
  let previous = 0
  for (let index = 0; index < data.length; index += 1) {
    const white = Math.random() * 2 - 1
    previous = previous * 0.985 + white * 0.015
    data[index] = previous * 2.4
  }
  return buffer
}

function scheduleCricket(graph: NatureAudioGraph) {
  const now = graph.context.currentTime
  const chirps = 2 + Math.floor(Math.random() * 3)
  for (let index = 0; index < chirps; index += 1) {
    const start = now + index * 0.09
    const oscillator = graph.context.createOscillator()
    const gain = graph.context.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(4_200 + Math.random() * 900, start)
    gain.gain.setValueAtTime(0.0001, start)
    gain.gain.exponentialRampToValueAtTime(0.012, start + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.055)
    oscillator.connect(gain).connect(graph.ambience)
    oscillator.start(start)
    oscillator.stop(start + 0.07)
  }
}

function applyPeriod(graph: NatureAudioGraph, period: FishingNaturePeriod) {
  const night = period === FishingNaturePeriod.AfterSunset
  const now = graph.context.currentTime
  graph.filter.frequency.setTargetAtTime(night ? 720 : 1_350, now, 0.8)
  graph.ambience.gain.setTargetAtTime(night ? 0.16 : 0.1, now, 1.2)
  if (graph.cricketTimer !== null) window.clearInterval(graph.cricketTimer)
  graph.cricketTimer = null
  if (night) {
    scheduleCricket(graph)
    graph.cricketTimer = window.setInterval(() => scheduleCricket(graph), 1_800 + Math.random() * 900)
  }
}

export function useFishingNatureSound(period: FishingNaturePeriod) {
  const graphRef = useRef<NatureAudioGraph | null>(null)

  const start = useCallback(() => {
    if (graphRef.current) {
      void graphRef.current.context.resume().catch(() => undefined)
      return
    }
    const AudioContextConstructor = window.AudioContext
    if (!AudioContextConstructor) return
    const context = new AudioContextConstructor()
    const source = context.createBufferSource()
    const filter = context.createBiquadFilter()
    const ambience = context.createGain()
    const limiter = context.createDynamicsCompressor()
    source.buffer = makeNoiseBuffer(context)
    source.loop = true
    filter.type = 'lowpass'
    ambience.gain.value = 0.0001
    limiter.threshold.value = -18
    limiter.ratio.value = 4
    source.connect(filter).connect(ambience).connect(limiter).connect(context.destination)
    source.start()
    const graph: NatureAudioGraph = { context, ambience, filter, cricketTimer: null }
    graphRef.current = graph
    applyPeriod(graph, period)
    void context.resume().catch(() => undefined)
  }, [period])

  useEffect(() => {
    if (graphRef.current) applyPeriod(graphRef.current, period)
  }, [period])

  useEffect(() => () => {
    const graph = graphRef.current
    if (!graph) return
    if (graph.cricketTimer !== null) window.clearInterval(graph.cricketTimer)
    void graph.context.close().catch(() => undefined)
    graphRef.current = null
  }, [])

  return start
}
